import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Avatar, Box, Button, Checkbox, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'

import { format, parse } from 'date-fns'
import {
  loadTeamMembersStart,
  toggleSelectMember,
  setPage,
  setSort,
  toggleSelectOnePageMembers,
  toggleSelectAllMembers,
  setActionableMembers,
} from '../../../redux/modules/teamMembers'
import { selectMembersRows, selectTeamMembers } from '../../../redux/selectors/teamMembersSelectors'
import { publishRemoveMembers } from '../../../util/events/teamsEventPublishers'
import TableMembers from '../common/TableMembers'
import DialogSendGifts from '../perks/DialogSendGifts'
import { publishClickedSendGift } from '../../../util/events/teamsGiftsEventPublishers'
import { publishClickedCheckMember } from '../../../util/events/teamsMembersEventPublishers'
import { ACTION_TARGETS } from '../../../util/events/teamsEvents'
import ToolboxMemberActions from './ToolboxMemberActions'
import DialogRemoveMember from './DialogRemoveMember'
import { getMemberRole, getMemberStatus, isRowSelected, parseFullName } from './adminUtils'

const useStyles = makeStyles({
  fullName: {
    fontWeight: 500,
  },
})

const TableAdminMembers = ({
  teamMembers,
  rows,
  page,
  sort,
  isLoadingTeamMembers,
  selectedMembers,
  isWebpSupported,
  toggleSelectMember,
  toggleSelectOnePageMembers,
  toggleSelectAllMembers,
  setPage,
  setSort,
  setActionableMembers,
}) => {
  const showRemindFeature = false

  const classes = useStyles()
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [showGiftsDialog, setShowGiftsDialog] = useState(false)
  const selectedMemberIds = selectedMembers.map(sm => sm.userId)

  const handleClickSendGift = () => {
    setShowGiftsDialog(true)
    setActionableMembers()
    publishClickedSendGift(selectedMemberIds)
  }

  const handleClickRemove = () => {
    setShowRemoveDialog(true)
    setActionableMembers()
    publishRemoveMembers(selectedMemberIds)
  }

  const handleSelectMember = member => {
    publishClickedCheckMember(ACTION_TARGETS.CHECK_MEMBER, !isRowSelected(selectedMembers, member))

    toggleSelectMember(member)
  }

  const handleSelectOnePage = checked => {
    publishClickedCheckMember(ACTION_TARGETS.CHECK_PAGE_MEMBERS, checked)

    toggleSelectOnePageMembers({ checked, rows })
  }

  const handleSelectAll = checked => {
    publishClickedCheckMember(ACTION_TARGETS.CHECK_ALL_MEMBERS, !checked) // TODO Fix this checked status (it's inverse): make it consistent with the select all page above

    toggleSelectAllMembers({ checked, allMembers: teamMembers })
  }

  const columns = [
    {
      id: 'avatarId',
      label: '',
      sortable: false,
    },
    {
      id: 'lastName', // This field is named lastName for sorting purposes
      label: 'Name',
      sortable: true,
    },
    {
      id: 'confirmedAt',
      label: 'Joined',
      sortable: false,
    },
    {
      id: 'isAdmin',
      label: 'Role',
      sortable: true,
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
    },
    {
      id: 'reminder',
      label: '',
      sortable: false,
    },
  ]

  const rowCells = rows.map((member, index) => {
    const labelId = `members-table-checkbox-${index}`
    return {
      memberId: member.userId,
      cells: [
        {
          Component: (
            <Checkbox
              key={member.userId}
              checked={isRowSelected(selectedMembers, member)}
              inputProps={{ 'aria-labelledby': labelId }}
              onClick={() => handleSelectMember(member)}
            />
          ),
        },
        {
          Component: (
            <Avatar
              key={member.userId}
              src={
                member.avatarId &&
                `${process.env.REACT_APP_IMAGE_LAMBDA}/profiles/${member.avatarId}.${
                  isWebpSupported ? 'webp' : 'jpeg'
                }?height=50&width=50`
              }
            />
          ),
        },
        {
          labelId: labelId,
          Component: (
            <Box key={member.userId}>
              <Typography className={classes.fullName}>{parseFullName(member)}</Typography>
              <Typography variant="body2" color="textSecondary">
                {member.email}
              </Typography>
            </Box>
          ),
        },
        {
          Component: (
            <>{member.confirmedAt ? format(parse(member.confirmedAt), 'MM/DD/YY') : '-'}</>
          ),
        },
        {
          Component: <>{getMemberRole(member.isAdmin)}</>,
        },
        {
          Component: <>{getMemberStatus(member.status)}</>,
        },
        {
          Component: (
            <>
              {showRemindFeature && member.status === 'INVITED' && (
                <Button color="primary">Remind</Button>
              )}
            </>
          ),
        },
      ],
    }
  })

  return (
    <>
      <TableMembers
        checkbox
        columns={columns}
        teamMembers={teamMembers}
        selectedMembers={selectedMembers}
        rows={rows}
        rowCells={rowCells}
        page={page}
        sort={sort}
        onSelectOnePage={handleSelectOnePage}
        onSelectAll={handleSelectAll}
        setPage={setPage}
        setSort={setSort}
        loading={isLoadingTeamMembers}
        Toolbox={
          <ToolboxMemberActions
            onShowGiftsDialog={handleClickSendGift}
            onShowRemoveDialog={handleClickRemove}
          />
        }
      />
      {showRemoveDialog && <DialogRemoveMember onClose={() => setShowRemoveDialog(false)} />}
      {showGiftsDialog && <DialogSendGifts onClose={() => setShowGiftsDialog(false)} />}
    </>
  )
}

const mapStateToProps = state => {
  const teamMembers = selectTeamMembers(state)
  const rows = selectMembersRows(state)
  const { isLoadingTeamMembers, selectedMembers, page, search, sort } = state.teamMembers
  const { isWebpSupported } = state.browser

  return {
    teamMembers,
    rows,
    isLoadingTeamMembers,
    selectedMembers,
    isWebpSupported,
    page,
    search,
    sort,
  }
}

const mapDispatchToProps = {
  loadTeamMembersStart,
  toggleSelectMember,
  toggleSelectOnePageMembers,
  toggleSelectAllMembers,
  setPage,
  setSort,
  setActionableMembers,
}

export default connect(mapStateToProps, mapDispatchToProps)(TableAdminMembers)
